import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  setAllGroupUser,
  setGroupUser,
} from "../../../redux/TaskGroupingSlice";
import { useUsers } from "../../../reactQuery/users";
import {
  createExpandedTeamPanels,
  getFilteredTeamUsers,
  getSearchExpandedTeamPanels,
  getUnassignedUsers,
  normalizeTeams,
} from "../utils/myTaskUtils";
import {
  buildAllocationUsersForSelection,
  buildUserSelectionSearchParams,
  parseSelectedUserIds,
} from "../utils/teamFilterUtils";

export function useTeamFilterState(team) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { users } = useUsers();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [openUnassigned, setUnassigned] = React.useState(false);
  const [expandedPanel, setExpandedPanel] = React.useState([]);
  const [wasSearching, setWasSearching] = React.useState(false);
  const [syncedUidParam, setSyncedUidParam] = React.useState("");

  const groupingUser = useSelector((state) => state.groupingUser.allocationUsers);

  const selectedUsers = React.useMemo(
    () => groupingUser?.map((item) => item?.users).flat() || [],
    [groupingUser]
  );
  const selectedUserIdsInRedux = React.useMemo(
    () => selectedUsers.map((u) => String(u.user_id)),
    [selectedUsers]
  );

  const open = Boolean(anchorEl);

  const selectedUserIds = React.useMemo(
    () => parseSelectedUserIds(searchParams.get("uid") || ""),
    [searchParams]
  );

  const selectedUserIdsStr = React.useMemo(
    () => selectedUserIds.join(","),
    [selectedUserIds]
  );
  const selectedUserIdsInReduxStr = React.useMemo(
    () => selectedUserIdsInRedux.join(","),
    [selectedUserIdsInRedux]
  );

  const currentUidParam = searchParams.get("uid") || "";
  const availableUsers = users?.data?.data?.responseData || [];
  const querySelectedUsers = React.useMemo(() => {
    if (!selectedUserIds.length) { 
      return [];
    }

    const userMap = new Map(
      [...selectedUsers, ...availableUsers].map((user) => [
        String(user.user_id),
        user,
      ])
    );

    return selectedUserIds
      .map((userId) => userMap.get(String(userId)))
      .filter(Boolean);
  }, [availableUsers, selectedUsers, selectedUserIds]); 

  React.useEffect(() => {
    if (
      currentUidParam === syncedUidParam ||
      !currentUidParam ||
      selectedUserIdsStr === selectedUserIdsInReduxStr
    ) {
      if (currentUidParam === "") setSyncedUidParam("");
      return;
    }

    if (!querySelectedUsers.length) {
      setSyncedUidParam(currentUidParam);
      return;
    }

    dispatch(
      setAllGroupUser(
        buildAllocationUsersForSelection({
          selectedUserIds,
          candidateUsers: querySelectedUsers,
        })
      )
    );
    setSyncedUidParam(currentUidParam);
  }, [
    currentUidParam,
    dispatch,
    querySelectedUsers,
    selectedUserIdsStr,
    selectedUserIdsInReduxStr,
    selectedUserIds,
    syncedUidParam,
  ]);

  // -------------------------

  const effectiveSelectedUsers = React.useMemo(() => {
    return currentUidParam && syncedUidParam !== currentUidParam
      ? querySelectedUsers
      : selectedUsers;
  }, [currentUidParam, querySelectedUsers, selectedUsers, syncedUidParam]);

  const allTeams = React.useMemo(
    () => normalizeTeams(team?.data?.data?.responseData),
    [team?.data?.data?.responseData]
  );

  const filteredItems = React.useMemo(
    () =>
      getFilteredTeamUsers({
        users: availableUsers,
        teams: allTeams,
        searchQuery,
      }),
    [allTeams, availableUsers, searchQuery]
  );

  const unAssignedTeam = React.useMemo(
    () =>
      getUnassignedUsers({
        filteredItems,
        selectedUsers: effectiveSelectedUsers,
      }),
    [effectiveSelectedUsers, filteredItems]
  );

  const allUser = React.useMemo(
    () => filteredItems.filter((user) => user.active) || [],
    [filteredItems]
  );

  const visibleTeams = React.useMemo(
    () =>
      allTeams.filter(
        (teamItem) =>
          teamItem?.users?.length &&
          teamItem.users.some((teamUser) =>
            filteredItems.some((user) => user.user_id === teamUser.user_id)
          )
      ),
    [allTeams, filteredItems]
  );

  const handleExpandAccordianChange = React.useCallback(
    (teamId) => (_event, isExpanded) => {
      setExpandedPanel((prev) =>
        prev.map((element) =>
          element.team_id === teamId
            ? { ...element, isExpand: isExpanded }
            : element
        )
      );
    },
    []
  );

  React.useEffect(() => {
    if (allTeams.length) {
      setExpandedPanel(createExpandedTeamPanels(allTeams));
    }
  }, [allTeams]);

  React.useEffect(() => {
    if (searchQuery) {
      setWasSearching(true);
      setExpandedPanel(
        getSearchExpandedTeamPanels({
          teams: allTeams,
          filteredItems,
        })
      );
      if (unAssignedTeam.length > 0) {
        setUnassigned(true);
      }
      return;
    }

    if (wasSearching) {
      setWasSearching(false);
      setUnassigned(false);
      setExpandedPanel(createExpandedTeamPanels(allTeams));
    }
  }, [allTeams, filteredItems, searchQuery, unAssignedTeam.length, wasSearching]);

  const setTeam = React.useCallback(
    (teamUser, isChecked) => {
      dispatch(setAllGroupUser([]));

      if (isChecked) {
        selectedUsers.forEach((element) => {
          if (element.team_id !== teamUser.team_id) {
            dispatch(setGroupUser(element));
          }
        });
        return;
      }

      teamUser.users.forEach((element) => {
        dispatch(setGroupUser(element));
      });

      selectedUsers.forEach((element) => {
        if (element.team_id !== teamUser.team_id) {
          dispatch(setGroupUser(element));
        }
      });
    },
    [dispatch, selectedUsers]
  );

  const syncSelectedUsers = React.useCallback(
    (nextSelectedUserIds, candidateUsers = []) => {
      const nextSearchParams = buildUserSelectionSearchParams(
        searchParams,
        nextSelectedUserIds
      );

      setSearchParams(nextSearchParams);
      dispatch(
        setAllGroupUser(
          buildAllocationUsersForSelection({
            selectedUserIds: nextSelectedUserIds,
            candidateUsers: [
              ...effectiveSelectedUsers,
              ...selectedUsers,
              ...allUser,
              ...candidateUsers,
            ],
          })
        )
      );
    },
    [allUser, dispatch, effectiveSelectedUsers, searchParams, selectedUsers, setSearchParams]
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
    setSearchQuery("");
  }, []);

  const selectAll = React.useCallback(() => {
    syncSelectedUsers(
      selectedUserIds.length === allUser.length
        ? []
        : allUser.map((user) => String(user.user_id)),
      allUser
    );
  }, [allUser, selectedUserIds.length, syncSelectedUsers]);

  return {
    anchorEl,
    open,
    searchQuery,
    openUnassigned,
    expandedPanel,
    selectedUsers: effectiveSelectedUsers,
    allTeams,
    filteredItems,
    unAssignedTeam,
    allUser,
    visibleTeams,
    selectedUserIds,
    setAnchorEl,
    setSearchQuery,
    setUnassigned,
    handleExpandAccordianChange,
    setTeam,
    syncSelectedUsers,
    handleClose,
    selectAll,
  };
}
